import { createApp, createSSRApp, nextTick } from 'vue';
import { createHead } from '@vueuse/head';
import App from './App.vue';
import { SNAPSHOT_ATTR, canHydrate, normalizePath } from './hydration';
import router from './router';
import { store } from './store';
import Toast, { useToast } from 'vue-toastification';
import 'vue-toastification/dist/index.css';

import './assets/styles.css';
import './assets/riso.css';

// Import Font Awesome core
import { library } from '@fortawesome/fontawesome-svg-core';

// Import specific icons
import { faShoppingCart, faEnvelope, faTrash, faTimes,
    faTrashAlt, faDollarSign, faCalendarAlt, faStar, faFaceSmile,
    faWandMagicSparkles, faHeart, faCircleInfo, faPlus, faCheck,
    faPaperPlane, faLink, faShareAlt, faPen, faArrowRight } from '@fortawesome/free-solid-svg-icons';

// Import brand icons for social sharing
import { faXTwitter, faFacebookF } from '@fortawesome/free-brands-svg-icons';

// Import the FontAwesomeIcon component
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

// Add icons to the library
library.add(faShoppingCart, faEnvelope, faTrash, faTimes, faTrashAlt, faDollarSign,
    faCalendarAlt, faStar, faFaceSmile, faWandMagicSparkles, faHeart, faCircleInfo,
    faPlus, faCheck, faPaperPlane, faLink, faShareAlt, faPen, faArrowRight,
    faXTwitter, faFacebookF);

// Mount SYNCHRONOUSLY so Google's JS-rendering pass (and any future prerenderer)
// always gets rendered HTML fast — then hydrate cart/postcard state afterwards.
// Previously the app mounted only after Promise.all([initializeCart,
// initializePostcard]) resolved, so a slow/failed initializer could leave the
// page an empty shell for the crawler.
//
// createSSRApp (not createApp) so Vue HYDRATES the prerendered DOM in place
// instead of doing container.innerHTML="" + a full re-render. Plain createApp
// wiped the prerendered markup on mount, collapsing the page to zero height and
// rebuilding it — a ~0.48 CLS spike (footer h305→h0) that regressed Core Web
// Vitals the day prerendering shipped.
//
// …but ONLY when the markup in #app is this route's markup. Hosting rewrites
// every unknown path to /index.html — the prerendered LANDING page — so a deep
// link to an un-prerendered route (/assistant, /membership, /mine/<token>,
// /agent-checkout/<id>) arrives with the wrong snapshot. Hydrating that does not
// fall back cleanly: Vue patches the new page INTO the leftover DOM, and the
// whole route ends up nested inside the landing page's first decorative
// `.mail-float` div (position:fixed, opacity:.09) — an invisible page. Every
// emailed recipient link and every agent checkout link landed there.
//
// So the prerenderer stamps each snapshot with the path it was rendered for
// (below, before render-event), and we hydrate only on an exact match.
const appEl = document.getElementById('app');
const snapshotPath = appEl && appEl.getAttribute(SNAPSHOT_ATTR);
const hydratable = canHydrate(snapshotPath, window.location.pathname);

// A snapshot for another route is worse than no snapshot. The pre-paint guard
// inlined in index.html has normally dropped it already (before it can flash);
// this is the belt-and-braces for a cached page that predates that guard.
if (appEl && appEl.firstChild && !hydratable) appEl.innerHTML = '';

const app = hydratable ? createSSRApp(App) : createApp(App);
const head = createHead();

app.component('font-awesome-icon', FontAwesomeIcon);
app.use(Toast, {
    position: 'top-right',
    timeout: 3000,
    closeOnClick: true,
    pauseOnHover: true,
});
app.use(store);
app.use(router);
app.use(head);

// Make toast available globally for Options API components
app.config.globalProperties.$toast = useToast();

// Mount only AFTER the initial route resolves. Mounting earlier renders an empty
// <router-view> for a tick; against prerendered markup that's a hydration mismatch,
// which makes Vue discard the whole snapshot and client-render it (the page-collapse
// CLS spike). router.isReady() resolves in a microtask for the statically-imported
// public routes, so this stays fast for crawlers.
router.isReady().then(() => {
    app.mount('#app');
    // Signal the build-time prerenderer that the route is fully rendered (after
    // @vueuse/head has flushed head tags). Harmless in the browser — no runtime
    // listener. Paired with renderAfterDocumentEvent in vue.config.js.
    nextTick(() => {
        // Stamp the path this markup belongs to, so the snapshot the prerenderer
        // is about to capture can only be hydrated on the route it was rendered
        // for. Set before render-event so it is part of the captured HTML.
        if (appEl) appEl.setAttribute(SNAPSHOT_ATTR, normalizePath(window.location.pathname));
        document.dispatchEvent(new Event('render-event'));
    });
});

// Hydrate persisted state after mount; never blocks the first render.
Promise.all([
    store.dispatch('initializeCart'),
    store.dispatch('initializePostcard')
]).catch((error) => {
    console.error('Error initializing app state:', error);
});
